/**
 * AEGIS-13 voice scenario seed.
 *
 * Three hand-crafted scenarios so the voice demo can walk through real
 * data end-to-end:
 *   A. Morning briefing — Dallas late load (1842) to Ulta Beauty via
 *      Schneider, plus a pickup today (1901) and the Memphis exception.
 *   B. Inventory risk — SKU-441 Hydraulic Fluid 3-day supply, PO-2241
 *      Grainger arriving 4 days after stockout.
 *   C. Exception handling — Memphis short delivery (load 1976) to RWC
 *      Industries, 4 cases short on BOL of 52.
 *
 * Each record follows the standard schema fields used elsewhere in the
 * demo tenant so the existing dashboards pick them up without any
 * special-case handling.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const nowIso = () => new Date().toISOString();
const iso = (daysOffset) => new Date(Date.now() + daysOffset * 86400000).toISOString();

const base = {
  tenantId: DEMO_TENANT_ID,
  createdAt: nowIso(),
  createdBy: 'seed',
  updatedAt: nowIso(),
  updatedBy: 'seed',
  isDeleted: false,
  version: 1,
};

// ── Loads ──────────────────────────────────────────────────────────────
export const SEED_VOICE_LOADS = [
  {
    ...base,
    id: 'load-voice-1842',
    loadNumber: 'WC-VOICE-1842',
    mode: 'OTR',
    status: 'in_transit',
    customerName: 'Ulta Beauty',
    customerId: 'cust-voice-ulta',
    carrierName: 'Schneider National',
    carrierId: 'carrier-voice-schneider',
    carrierScac: 'SCHN',
    equipmentType: 'Dry Van 53ft',
    commodity: 'Health & Beauty',
    weight: 42000,
    origin: { city: 'Houston', state: 'TX' },
    destination: { city: 'Dallas', state: 'TX' },
    miles: 239,
    pickupDate: iso(-1),
    estimatedDelivery: iso(0),   // today
    deliveryWindow: { close: '15:00' },
    lastGpsPing: iso(-0.12),      // ~3 hours ago
    lateMinutes: 120,
    checkCallWindow: '06:00',
    driverConfirmed: false,
    driver: { driverName: 'Tony Reyes', driverPhone: '(555) 555-0122' },
    financials: { customerRate: 2650, carrierRate: 2050, margin: 600, marginPercent: 22.6 },
    aegisFlags: ['late_no_check_call', 'hard_dock_close_risk'],
    slaThresholdPct: 90,
  },
  {
    ...base,
    id: 'load-voice-1901',
    loadNumber: 'WC-VOICE-1901',
    mode: 'OTR',
    status: 'scheduled',
    customerName: 'Acme Distribution',
    customerId: 'cust-voice-acme',
    carrierName: 'JB Hunt',
    carrierId: 'carrier-voice-jbhunt',
    carrierScac: 'JBHT',
    equipmentType: 'Dry Van 53ft',
    commodity: 'CPG',
    weight: 38500,
    origin: { city: 'Memphis', state: 'TN' },
    destination: { city: 'Chicago', state: 'IL' },
    miles: 532,
    pickupDate: iso(0),
    pickupAppointment: new Date(Date.now() + 90 * 60 * 1000).toISOString(),  // 90 min from now
    driverConfirmed: false,
    aegisFlags: ['pickup_not_confirmed'],
  },
  {
    ...base,
    id: 'load-voice-1976',
    loadNumber: 'WC-VOICE-1976',
    mode: 'OTR',
    status: 'delivered',
    customerName: 'RWC Industries',
    customerId: 'cust-voice-rwc',
    carrierName: 'XPO Logistics',
    carrierId: 'carrier-voice-xpo',
    carrierScac: 'XPOL',
    equipmentType: 'Dry Van 53ft',
    commodity: 'Industrial',
    weight: 36200,
    origin: { city: 'St. Louis', state: 'MO' },
    destination: { city: 'Memphis', state: 'TN' },
    miles: 288,
    pickupDate: iso(-2),
    deliveryDate: iso(-0.4),
    casesBol: 52,
    casesDelivered: 48,
    pickRecord: { casesPicked: 52, pickedBy: 'user-luis-picker', pickedAt: iso(-2.1) },
    dockOutboundScan: { scannedCases: 52, scannedAt: iso(-2.05) },
    aegisFlags: ['short_delivery_reported', 'transit_shortage_suspected'],
  },
];

// ── Customers (contact info AEGIS needs to draft outreach) ─────────────
export const SEED_VOICE_CUSTOMERS = [
  {
    ...base,
    id: 'cust-voice-ulta',
    customerId: 'cust-voice-ulta',
    name: 'Ulta Beauty',
    type: 'enterprise',
    slaThresholdPct: 90,
    monthToDateOnTimePct: 83,
    contacts: [
      { name: 'Jamie Roland',   email: 'jamie.roland@ulta.com',   phone: '(312) 555-0144', role: 'DC Manager' },
      { name: 'Priya Shah',     email: 'priya.shah@ulta.com',     phone: '(312) 555-0145', role: 'Logistics Director' },
    ],
    contractExpiresAt: iso(21),
  },
  {
    ...base,
    id: 'cust-voice-rwc',
    customerId: 'cust-voice-rwc',
    name: 'RWC Industries',
    type: 'mid_market',
    contacts: [
      { name: 'Tom Hendricks', email: 'tom.hendricks@rwcindustries.com', phone: '(901) 555-0188', role: 'Receiving Manager' },
      { name: 'Sarah Mills',   email: 'sarah.mills@rwcindustries.com',   phone: '(901) 555-0189', role: 'Plant Ops' },
    ],
    contractExpiresAt: iso(18),
  },
];

// ── Inventory (IMS — SKUs referenced in scenario B) ────────────────────
export const SEED_VOICE_INVENTORY = [
  {
    ...base,
    id: 'inv-voice-441',
    sku: 'SKU-441',
    name: 'Hydraulic Fluid, 5-gal',
    onHand: 42,
    dailyVelocity: 14,
    daysOfSupply: 3,
    primarySupplier: 'Grainger',
    openPoId: 'po-voice-2241',
    expectedReceiptDate: iso(7),
    expectedStockoutDate: iso(3),
    riskLevel: 'high',
  },
  {
    ...base,
    id: 'inv-voice-118',
    sku: 'SKU-118',
    name: 'Industrial Solvent, 1-gal',
    onHand: 60,
    dailyVelocity: 12,
    daysOfSupply: 5,
    primarySupplier: 'Grainger',
    riskLevel: 'medium',
  },
  {
    ...base,
    id: 'inv-voice-302',
    sku: 'SKU-302',
    name: 'Safety Gloves, Large',
    onHand: 192,
    dailyVelocity: 32,
    daysOfSupply: 6,
    primarySupplier: 'Uline',
    riskLevel: 'medium',
  },
];

// ── Purchase orders ────────────────────────────────────────────────────
export const SEED_VOICE_PURCHASE_ORDERS = [
  {
    ...base,
    id: 'po-voice-2241',
    poNumber: 'PO-2241',
    supplier: 'Grainger',
    sku: 'SKU-441',
    quantity: 200,
    status: 'open',
    expectedReceiptDate: iso(7),
    expeditedOption: {
      available: true,
      estimatedDaysToReceipt: 2,
      additionalCostUsd: 340,
    },
  },
];

// ── Exceptions ─────────────────────────────────────────────────────────
export const SEED_VOICE_EXCEPTIONS = [
  {
    ...base,
    id: 'exc-voice-1976',
    code: 'SHORT_DELIVERY',
    severity: 'high',
    status: 'open',
    loadId: 'load-voice-1976',
    customerId: 'cust-voice-rwc',
    carrierId: 'carrier-voice-xpo',
    description: 'RWC Industries received 48 cases of 52 on BOL — 4 case shortage, value approx $1,840.',
    casesShort: 4,
    valueUsd: 1840,
    openedAt: iso(-0.3),
    evidence: [
      { kind: 'wms_pick_record', detail: '52 cases picked 6:14 AM', ref: 'wms-pick-1976' },
      { kind: 'dock_outbound_scan', detail: '52 cases scanned at dock', ref: 'dock-scan-1976' },
    ],
    aegisAssessment: 'Transit shortage likely — warehouse and outbound scan both show 52 cases. Carrier claim appropriate.',
  },
];
